import * as d3 from 'd3';
import React from 'react';
import styled from 'styled-components';

import {useTranslation} from "react-i18next";
import Colors from '../../assets/colors/Colors';

interface Country {
    name: string,
    gip: number,
    alpha2: string,
    alpha3: string
}

interface Props {
    countries: Country[],
    maxGip: number,
    firstPageLength: number,
    isLife?: boolean
}

interface VerticalAxisProps {
    scale: d3.ScaleLinear<number, number>
}

interface BarProps {
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    index: number,
    country: Country,
    countriesLength: number,
    firstPageLength: number
}

const MARGIN = {
    top: 55,
    bottom: 40,
    left: 150,
    right: 40
};

const CHART_WIDTH = 1100 - MARGIN.left - MARGIN.right;
const CHART_HEIGHT = 600 - MARGIN.top - MARGIN.bottom;

const MIN_BAR_HEIGHT = 15;
const BAR_WIDTH = 80;


const StyledBarSelector = styled.path`
  fill: none;
`;

const StyledBarLabel = styled.text`
  font-size: 19;
  font-family: Mapfre-Light;
`;

const StyledAxisLabel = styled.text`
  fill: white;
  font-size: 19;
  font-family: Mapfre-Light;
  text-anchor: middle;
`;

const Bar = (props: BarProps) => {
    const {t, i18n} = useTranslation();

    const c = props.country;
    const scaledGip = props.yScale(c.gip);

    /////////// CHANGE HEREE ///////////////////////////////
    const widthScale = d3.scaleLinear()
        .domain([5, 12])
        .range([80, 30]);

    const borderRadiusScale = d3.scaleLinear()
        .domain([5, 12])
        .range([40, 15]);

    const barWidth = widthScale(Math.max(5, props.firstPageLength));
    const barX = MARGIN.left + props.xScale(props.index + 0.5);
    const barMidX = barX + barWidth / 2;
    const barHeight = scaledGip > MIN_BAR_HEIGHT ? scaledGip : MIN_BAR_HEIGHT;
    const barY = MARGIN.top + CHART_HEIGHT - barHeight;
    const barBorderRadius = barHeight > 30 ? borderRadiusScale(Math.max(5, props.firstPageLength)) : 7;

    return (
        <g key={`${c.name}-bar`}>
            <rect
                width={barWidth}
                height={barHeight}
                x={barX}
                y={barY}
                rx={barBorderRadius}
                ry={barBorderRadius}
            />
            <StyledBarSelector
                d={`m ${barMidX} ${barY} l 0 -15 l 50 -30`}
            />
            <StyledBarLabel
                x={`${barMidX + 55}px`}
                y={`${barY - 40}px`}>
                {c.gip.toFixed(2)}
            </StyledBarLabel>
            <StyledAxisLabel
                x={`${barMidX}px`}
                y={`${barY + barHeight + 32}px`}>
                {t(`countries.names.${c.alpha2}`)}
            </StyledAxisLabel>
        </g>
    )
};

const VerticalAxis = (props: VerticalAxisProps) => {
    const scale = props.scale;
    const ticks = scale.ticks();

    return (
        <React.Fragment>
            <path
                style={{
                    strokeWidth: '3px',
                    strokeLinecap: 'butt',
                    fill: 'none'
                }}
                d={`M150,${MARGIN.top}L150,${MARGIN.top + CHART_HEIGHT}`}
            />
            {ticks.map(tick => (
                <StyledAxisLabel
                    x="140"
                    y={MARGIN.top + CHART_HEIGHT - scale(tick)}
                    style={{
                        fill: '#FFFFFF',
                        textAnchor: 'end'
                    }}
                >
                    {tick.toFixed(1)}
                </StyledAxisLabel>
            ))}
        </React.Fragment>
    )
};

const HorizontalAxis = () => {
    return (
        <path
            style={{
                strokeWidth: '3px',
                strokeLinecap: 'butt',
                fill: 'none'
            }}
            d={`M150,${MARGIN.top + CHART_HEIGHT}L${MARGIN.left + CHART_WIDTH},${MARGIN.top + CHART_HEIGHT}`}
        />
    );
};

const Bars = (props: Props) => {
    // const countries = props.countries.sort((a: Country, b:Country): number => b.gip - a.gip);
    const countries = props.countries;

    const xScale = d3.scaleLinear()
        .domain([0, countries.length + 1])
        .range([0, CHART_WIDTH]);

    const yScale = d3.scaleLinear()
        // @ts-ignore
        .domain([0, props.maxGip])
        .range([0, CHART_HEIGHT]);

    return (
        <React.Fragment>
            <VerticalAxis scale={yScale}/>
            <HorizontalAxis/>
            {countries.map((c: Country, i: number) => (
                <Bar
                    key={i}
                    xScale={xScale}
                    yScale={yScale}
                    index={i}
                    country={c}
                    countriesLength={countries.length}
                    firstPageLength={props.firstPageLength}
                />
            ))
            }
        </React.Fragment>
    )
};

const BarChart = (props: Props) => {
    return (
        <svg
          viewBox="0 0 1100 600"
          stroke={props.isLife ? Colors.fontWhite : Colors.fontGray}
          fill={props.isLife ? Colors.fontWhite : Colors.fontGray}
        >
            <Bars {...props} />
        </svg>
    )
};

export default BarChart;
